import React, { useEffect, useState, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import {
  Upload,
  Camera,
  FileText,
  CreditCard,
  Printer,
  Sparkles,
  Palette,
  CheckCircle2,
  AlertCircle,
  RefreshCw,
  Sliders,
  RotateCw,
  Eye,
  Layers,
  IndianRupee,
  Smartphone,
  Check,
  Crop,
  ChevronRight,
  ChevronLeft,
  ZoomIn,
  ShieldCheck,
  Info,
  Trash2,
  Copy,
  ArrowLeft,
  ArrowRight,
  Plus,
} from "lucide-react";
import { toast } from "sonner";
import { PDFDocument } from "pdf-lib";
import { api, formatApiError } from "@/lib/api";
import {
  createCanvas,
  loadImage,
  canvasToDataURL,
  canvasToBlob,
  rotateCanvas,
  enhanceImage,
  renderDocumentA4Layout,
  renderIdCardA4Layout,
  evaluateImageQuality,
} from "@/lib/imageEnhancer";
import { renderPdfToCanvases } from "@/lib/pdfLoader";
import DocumentCropEditor from "@/components/DocumentCropEditor";

const PRESETS = [
  { id: "original", label: "Original" },
  { id: "auto", label: "Auto Enhance" },
  { id: "document", label: "Magic Color" },
  { id: "bw", label: "B&W Xerox" },
  { id: "clean_bg", label: "Clean Background" },
  { id: "grayscale", label: "Grayscale" },
  { id: "sharp_doc", label: "Sharp Document" },
];

function isPdfFile(file) {
  if (!file) return false;
  const name = (file.name || "").toLowerCase();
  const type = (file.type || "").toLowerCase();
  return type === "application/pdf" || name.endsWith(".pdf") || type.includes("pdf");
}

export default function ScanToPrintCustomer() {
  const { shopId } = useParams();

  const [loading, setLoading] = useState(true);
  const [shop, setShop] = useState(null);
  const [error, setError] = useState("");

  // Customer Step:
  // 1: Upload / Capture
  // 2: Enhance & Adjust
  // 3: Layout & Print Settings
  // 4: Submitted Confirmation
  const [currentStep, setCurrentStep] = useState(1);

  // Mode: 'document' (A4 Document) | 'id_card' (Front + Back ID Card)
  const [mode, setMode] = useState("document");

  // Raw file tracking (CRITICAL: Preserves original PDF without conversion)
  const [rawUploadedFile, setRawUploadedFile] = useState(null);
  const [isPdfUpload, setIsPdfUpload] = useState(false);
  const [hasUserEditedDocument, setHasUserEditedDocument] = useState(false);

  // Document Pages
  const [pages, setPages] = useState([]);
  const [activePageIndex, setActivePageIndex] = useState(0);

  // ID Card slots
  const [idFront, setIdFront] = useState(null);
  const [idBack, setIdBack] = useState(null);
  const [activeIdSlot, setActiveIdSlot] = useState("front"); // 'front' | 'back'

  // Active Crop Editor Modal state
  const [cropEditorItem, setCropEditorItem] = useState(null);

  // Layout options
  const [a4FitMode, setA4FitMode] = useState("fit"); // 'fit' | 'fill'
  const [a4MarginMm, setA4MarginMm] = useState(6);
  const [a4Orientation, setA4Orientation] = useState("portrait");
  const [idLayout, setIdLayout] = useState("stacked"); // 'stacked' | 'side_by_side'
  const [showCutGuides, setShowCutGuides] = useState(true);

  // Print settings
  const [printType, setPrintType] = useState("bw"); // 'bw' | 'color'
  const [copies, setCopies] = useState(1);

  // Before / After inspection
  const [isHoldingOriginal, setIsHoldingOriginal] = useState(false);

  // Fine adjust sliders drawer toggle
  const [showFineSliders, setShowFineSliders] = useState(false);

  // Submission state
  const [submitting, setSubmitting] = useState(false);
  const [submittedJob, setSubmittedJob] = useState(null);

  const [isLoadingFile, setIsLoadingFile] = useState(false);

  // File input refs
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  // -------------------------------------------------------------
  // Load Public Shop Info
  // -------------------------------------------------------------
  useEffect(() => {
    loadShop();
  }, [shopId]);

  const loadShop = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get(`/scan-to-print/shop/${encodeURIComponent(shopId)}`);
      if (res.data?.shop) {
        setShop(res.data.shop);
      } else {
        throw new Error("Shop information not found.");
      }
    } catch (err) {
      setError(formatApiError(err) || "Unable to connect to PrintQ service. Please scan the QR again.");
    } finally {
      setLoading(false);
    }
  };

  // -------------------------------------------------------------
  // Non-destructive enhancement pipeline
  // -------------------------------------------------------------
  const applyEnhancements = (item) => {
    if (!item?.processedSourceCanvas && !item?.originalCanvas) return item;
    const base = item.processedSourceCanvas || item.originalCanvas;

    const enhanced = enhanceImage(base, {
      preset: item.preset || "original",
      brightness: item.brightness || 0,
      contrast: item.contrast || 0,
      sharpness: item.sharpness || 0,
      threshold: item.threshold ?? null,
    });

    const quality = evaluateImageQuality(enhanced, mode);

    return {
      ...item,
      enhancedCanvas: enhanced,
      quality,
      previewUrl: canvasToDataURL(enhanced, "image/jpeg", 0.92),
    };
  };

  const activeItem = useMemo(() => {
    if (mode === "document") {
      return pages[activePageIndex] || null;
    }
    return activeIdSlot === "front" ? idFront : idBack;
  }, [mode, pages, activePageIndex, activeIdSlot, idFront, idBack]);

  const updateActiveItem = (updates) => {
    setHasUserEditedDocument(true);
    if (mode === "document") {
      setPages((prev) => {
        if (!prev[activePageIndex]) return prev;
        const updated = applyEnhancements({ ...prev[activePageIndex], ...updates });
        const next = [...prev];
        next[activePageIndex] = updated;
        return next;
      });
    } else {
      if (activeIdSlot === "front") {
        setIdFront((curr) => (curr ? applyEnhancements({ ...curr, ...updates }) : curr));
      } else {
        setIdBack((curr) => (curr ? applyEnhancements({ ...curr, ...updates }) : curr));
      }
    }
  };

  // Apply current filter & adjustments to all pages
  const handleApplyToAllPages = () => {
    if (!activeItem || mode !== "document") return;
    setHasUserEditedDocument(true);
    const { preset, brightness, contrast, sharpness } = activeItem;
    setPages((prev) =>
      prev.map((item) =>
        applyEnhancements({
          ...item,
          preset,
          brightness,
          contrast,
          sharpness,
        })
      )
    );
    toast.success("Applied filter settings to all pages!");
  };

  // -------------------------------------------------------------
  // Page Management (Reorder, Rotate, Duplicate, Delete)
  // -------------------------------------------------------------
  const handleRotatePage = () => {
    if (!activeItem) return;
    setHasUserEditedDocument(true);
    const base = activeItem.processedSourceCanvas || activeItem.originalCanvas;
    const rotated = rotateCanvas(base, 90);
    updateActiveItem({
      processedSourceCanvas: rotated,
    });
  };

  const handleDuplicatePage = () => {
    if (!activeItem || mode !== "document") return;
    setHasUserEditedDocument(true);
    const duplicated = {
      ...activeItem,
      id: `copy-${Date.now()}`,
    };
    setPages((prev) => {
      const next = [...prev];
      next.splice(activePageIndex + 1, 0, duplicated);
      return next;
    });
    setActivePageIndex(activePageIndex + 1);
    toast.success("Page duplicated!");
  };

  const handleDeletePage = (indexToDelete) => {
    if (mode !== "document") return;
    setHasUserEditedDocument(true);
    setPages((prev) => {
      const next = prev.filter((_, i) => i !== indexToDelete);
      if (next.length === 0) {
        setCurrentStep(1);
      }
      return next;
    });
    if (activePageIndex >= indexToDelete && activePageIndex > 0) {
      setActivePageIndex(activePageIndex - 1);
    }
  };

  const handleMovePage = (direction) => {
    if (mode !== "document") return;
    setHasUserEditedDocument(true);
    const targetIndex = activePageIndex + direction;
    if (targetIndex < 0 || targetIndex >= pages.length) return;

    setPages((prev) => {
      const next = [...prev];
      const temp = next[activePageIndex];
      next[activePageIndex] = next[targetIndex];
      next[targetIndex] = temp;
      return next;
    });
    setActivePageIndex(targetIndex);
  };

  // -------------------------------------------------------------
  // File & Camera Ingestion (PDF + Image)
  // -------------------------------------------------------------
  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setIsLoadingFile(true);

    try {
      const newItems = [];
      const firstFile = files[0];
      const isPdf = isPdfFile(firstFile);

      setRawUploadedFile(firstFile);
      setIsPdfUpload(isPdf);
      setHasUserEditedDocument(false);

      for (let i = 0; i < files.length; i++) {
        const file = files[i];

        if (isPdfFile(file)) {
          // Centralized, safe PDF parser for high-res preview rendering
          const renderedPages = await renderPdfToCanvases(file, { scale: 2.0 });

          for (const p of renderedPages) {
            const item = applyEnhancements({
              id: `pdf-${Date.now()}-${p.pageNumber}`,
              originalCanvas: p.canvas,
              processedSourceCanvas: p.canvas,
              preset: "original", // Default to original for PDFs
              brightness: 0,
              contrast: 0,
              sharpness: 0,
            });
            newItems.push(item);
          }
        } else if (file.type.startsWith("image/") || /\.(jpe?g|png|webp|bmp|gif)$/i.test(file.name || "")) {
          const img = await loadImage(file);
          const canvas = createCanvas(img.naturalWidth, img.naturalHeight);
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0);

          const item = applyEnhancements({
            id: `img-${Date.now()}-${i}`,
            originalCanvas: canvas,
            processedSourceCanvas: canvas,
            preset: "original",
            brightness: 0,
            contrast: 0,
            sharpness: 0,
          });
          newItems.push(item);
        }
      }

      if (newItems.length === 0) {
        throw new Error("No readable pages or images found in file.");
      }

      if (mode === "document") {
        setPages(newItems);
        setActivePageIndex(0);
      } else {
        if (activeIdSlot === "front" || !idFront) {
          setIdFront(newItems[0]);
          if (newItems[1]) setIdBack(newItems[1]);
        } else {
          setIdBack(newItems[0]);
        }
      }

      // Automatically open Crop only on single uploaded camera photo (not on PDFs)
      if (newItems.length === 1 && !isPdf && mode === "id_card") {
        setCropEditorItem(newItems[0]);
      }

      setCurrentStep(2);
      toast.success(`Loaded ${newItems.length} page(s)!`);
    } catch (err) {
      console.error("File upload error:", err);
      toast.error("Failed to read document: " + (err.message || "Invalid file format"));
    } finally {
      setIsLoadingFile(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
      if (cameraInputRef.current) cameraInputRef.current.value = "";
    }
  };

  // -------------------------------------------------------------
  // Crop Confirmation Callback
  // -------------------------------------------------------------
  const handleCropConfirmed = ({ croppedCanvas, corners, sourceCanvas }) => {
    setHasUserEditedDocument(true);
    updateActiveItem({
      processedSourceCanvas: croppedCanvas,
      lastCropCorners: corners,
    });
    setCropEditorItem(null);
    toast.success("Perspective deskew & crop applied!");
  };

  // -------------------------------------------------------------
  // Rates & Cost Calculation
  // -------------------------------------------------------------
  const ratePerPage = useMemo(() => {
    if (!shop) return 0;
    return printType === "color"
      ? parseFloat(shop.color_rate || 20)
      : parseFloat(shop.bw_rate || 10);
  }, [shop, printType]);

  const totalSheets = useMemo(() => {
    if (mode === "document") {
      return Math.max(1, pages.length) * copies;
    }
    return 1 * copies;
  }, [mode, pages.length, copies]);

  const totalCost = useMemo(() => {
    return (totalSheets * ratePerPage).toFixed(2);
  }, [totalSheets, ratePerPage]);

  // -------------------------------------------------------------
  // Submit Final Print Job
  // CRITICAL: NEVER convert untouched PDF to JPG. Preserve original bytes!
  // -------------------------------------------------------------
  const handleSubmitJob = async () => {
    if (mode === "document" && pages.length === 0) {
      toast.error("Please choose a document to upload.");
      return;
    }
    if (mode === "id_card" && !idFront && !idBack) {
      toast.error("Please upload at least one side of the ID card.");
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("shop_id", shopId);
      formData.append("copies", String(copies));
      formData.append("print_type", printType);
      formData.append("pages", String(pages.length || 1));

      if (mode === "document") {
        if (isPdfUpload && !hasUserEditedDocument && rawUploadedFile) {
          // ========================================================
          // CASE A: UNTOUCHED ORIGINAL PDF
          // Submit 100% untouched raw PDF bytes! Zero rasterization!
          // ========================================================
          console.log("[PrintQ Submit] Sending untouched original PDF:", {
            name: rawUploadedFile.name,
            type: rawUploadedFile.type,
            size: rawUploadedFile.size,
          });

          formData.append(
            "file",
            rawUploadedFile,
            rawUploadedFile.name || "customer-document.pdf"
          );
        } else if (isPdfUpload && hasUserEditedDocument) {
          // ========================================================
          // CASE B: USER-MODIFIED MULTI-PAGE DOCUMENT
          // Pack modified pages into a clean, 300 DPI high-res PDF
          // ========================================================
          console.log("[PrintQ Submit] Generating enhanced 300 DPI multi-page PDF...");
          const pdfDoc = await PDFDocument.create();

          for (let pIdx = 0; pIdx < pages.length; pIdx++) {
            const pageItem = pages[pIdx];
            const a4Canvas = renderDocumentA4Layout(pageItem.enhancedCanvas, {
              fitMode: a4FitMode,
              marginMm: a4MarginMm,
              orientation: a4Orientation,
              dpi: 300,
            });

            const pageBlob = await canvasToBlob(a4Canvas, "image/jpeg", 0.95);
            const pageBytes = new Uint8Array(await pageBlob.arrayBuffer());
            const embeddedJpg = await pdfDoc.embedJpg(pageBytes);

            const pdfPage = pdfDoc.addPage([595.28, 841.89]); // A4 in points
            pdfPage.drawImage(embeddedJpg, {
              x: 0,
              y: 0,
              width: 595.28,
              height: 841.89,
            });
          }

          const finalPdfBytes = await pdfDoc.save();
          const finalPdfBlob = new Blob([finalPdfBytes], { type: "application/pdf" });
          const originalName = rawUploadedFile?.name || "document.pdf";
          const finalName = originalName.toLowerCase().endsWith(".pdf")
            ? originalName
            : `${originalName}.pdf`;

          formData.append("file", finalPdfBlob, finalName);
        } else if (!hasUserEditedDocument && rawUploadedFile) {
          // ========================================================
          // CASE C: UNTOUCHED ORIGINAL IMAGE (JPG / PNG / WEBP)
          // Preserves original image bytes & resolution exactly!
          // ========================================================
          console.log("[PrintQ Submit] Sending untouched original image:", {
            name: rawUploadedFile.name,
            type: rawUploadedFile.type,
            size: rawUploadedFile.size,
          });
          formData.append(
            "file",
            rawUploadedFile,
            rawUploadedFile.name || `photo-${Date.now()}.jpg`
          );
        } else {
          // ========================================================
          // CASE D: MODIFIED SINGLE IMAGE
          // ========================================================
          const printCanvas = renderDocumentA4Layout(activeItem?.enhancedCanvas, {
            fitMode: a4FitMode,
            marginMm: a4MarginMm,
            orientation: a4Orientation,
            dpi: 300,
          });
          const printBlob = await canvasToBlob(printCanvas, "image/jpeg", 0.95);
          formData.append("file", printBlob, `scan-${Date.now()}.jpg`);
        }
      } else {
        // ID Card mode
        const printCanvas = renderIdCardA4Layout(idFront?.enhancedCanvas, idBack?.enhancedCanvas, {
          layout: idLayout,
          showCuttingGuides: showCutGuides,
          dpi: 300,
        });
        const printBlob = await canvasToBlob(printCanvas, "image/jpeg", 0.95);
        formData.append("file", printBlob, `id-card-${Date.now()}.jpg`);
      }

      const res = await api.post("/scan-to-print/jobs", formData);
      if (res.data?.job) {
        setSubmittedJob(res.data.job);
        setCurrentStep(4);
        toast.success("Print job sent to shop counter!");
      }
    } catch (err) {
      console.error("Job submit error:", err);
      toast.error(formatApiError(err) || "Unable to connect to PrintQ. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 text-white flex flex-col items-center justify-center p-4">
        <RefreshCw className="w-8 h-8 animate-spin text-amber-400 mb-3" />
        <p className="text-sm font-bold tracking-tight">Connecting to PrintQ Counter...</p>
        <span className="text-xs text-zinc-500 mt-1">JSM DIGITAL SOLUTIONS</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-zinc-950 text-white flex flex-col items-center justify-center p-6 text-center">
        <div className="w-16 h-16 rounded-3xl bg-rose-500/10 border border-rose-500/30 flex items-center justify-center text-rose-400 mb-4">
          <AlertCircle className="w-8 h-8" />
        </div>
        <h2 className="text-lg font-black text-white">Connection Notice</h2>
        <p className="text-xs text-zinc-400 mt-2 max-w-sm leading-relaxed">{error}</p>
        <button
          onClick={loadShop}
          className="mt-6 px-6 py-2.5 rounded-xl bg-amber-500 text-zinc-950 font-bold text-xs hover:bg-amber-400 active:scale-95 transition-all"
        >
          Retry Connection
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-white flex flex-col justify-between selection:bg-amber-500/30">
      {/* Hidden File Inputs */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileUpload}
        accept="image/*,application/pdf"
        multiple={mode === "document"}
        className="hidden"
      />
      <input
        type="file"
        ref={cameraInputRef}
        onChange={handleFileUpload}
        accept="image/*"
        capture="environment"
        className="hidden"
      />

      {/* Top Header with Shop Name */}
      <header className="px-4 py-3 bg-zinc-900/90 border-b border-zinc-800 flex items-center justify-between sticky top-0 z-40 backdrop-blur-md">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center text-zinc-950 font-black text-sm shadow-md shadow-amber-500/20">
            Q
          </div>
          <div>
            <span className="text-[10px] font-bold text-amber-400 uppercase tracking-widest block leading-none">
              Scan to Print
            </span>
            <h1 className="text-xs font-black text-white tracking-tight leading-tight mt-0.5 truncate max-w-[200px] sm:max-w-md">
              {shop?.shop_name_english || "PrintQ Partner Shop"}
            </h1>
          </div>
        </div>

        {/* Step Indicator */}
        <div className="flex items-center gap-1 text-[11px] font-bold text-zinc-400">
          <span className={currentStep === 1 ? "text-amber-400 font-extrabold" : ""}>1. Upload</span>
          <span>→</span>
          <span className={currentStep === 2 ? "text-amber-400 font-extrabold" : ""}>2. Enhance</span>
          <span>→</span>
          <span className={currentStep === 3 ? "text-amber-400 font-extrabold" : ""}>3. Print</span>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="flex-1 max-w-2xl w-full mx-auto p-4 flex flex-col gap-5">
        {/* ============================================================ */}
        {/* STEP 1: UPLOAD / CAMERA SELECTION                             */}
        {/* ============================================================ */}
        {currentStep === 1 && (
          <div className="flex flex-col gap-5 animate-fade-in">
            {/* Mode Switcher: Document vs ID Card */}
            <div className="grid grid-cols-2 gap-3 bg-zinc-900/90 border border-zinc-800 p-1.5 rounded-2xl shadow-lg">
              <button
                type="button"
                onClick={() => setMode("document")}
                className={`flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-xs transition-all ${
                  mode === "document"
                    ? "bg-amber-500 text-zinc-950 shadow-md shadow-amber-500/20"
                    : "text-zinc-400 hover:text-white"
                }`}
              >
                <FileText className="w-4 h-4" />
                <span>A4 Document / PDF</span>
              </button>
              <button
                type="button"
                onClick={() => setMode("id_card")}
                className={`flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-xs transition-all ${
                  mode === "id_card"
                    ? "bg-amber-500 text-zinc-950 shadow-md shadow-amber-500/20"
                    : "text-zinc-400 hover:text-white"
                }`}
              >
                <CreditCard className="w-4 h-4" />
                <span>ID Card (CR80)</span>
              </button>
            </div>

            {/* Upload Action Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Camera Direct Scan */}
              <button
                type="button"
                onClick={() => cameraInputRef.current?.click()}
                disabled={isLoadingFile}
                className="bg-gradient-to-b from-zinc-900 to-zinc-950 border border-amber-500/30 hover:border-amber-400/60 p-6 rounded-3xl flex flex-col items-center justify-center gap-3 text-center transition-all active:scale-[0.98] shadow-xl group cursor-pointer"
              >
                <div className="w-14 h-14 rounded-2xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-400 group-hover:scale-110 transition-transform">
                  <Camera className="w-7 h-7" />
                </div>
                <div>
                  <h3 className="text-sm font-black text-white">Scan with Camera</h3>
                  <p className="text-[11px] text-zinc-400 mt-0.5">Auto document framing & capture</p>
                </div>
              </button>

              {/* Gallery / PDF Upload */}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={isLoadingFile}
                className="bg-gradient-to-b from-zinc-900 to-zinc-950 border border-zinc-800 hover:border-zinc-700 p-6 rounded-3xl flex flex-col items-center justify-center gap-3 text-center transition-all active:scale-[0.98] shadow-xl group cursor-pointer"
              >
                <div className="w-14 h-14 rounded-2xl bg-zinc-800 border border-zinc-700 flex items-center justify-center text-zinc-300 group-hover:scale-110 transition-transform">
                  {isLoadingFile ? <RefreshCw className="w-7 h-7 animate-spin text-amber-400" /> : <Upload className="w-7 h-7" />}
                </div>
                <div>
                  <h3 className="text-sm font-black text-white">Upload PDF / Photos</h3>
                  <p className="text-[11px] text-zinc-400 mt-0.5">Original Multi-Page PDFs, JPG, PNG</p>
                </div>
              </button>
            </div>

            {/* Rates Callout */}
            <div className="bg-zinc-900/60 border border-zinc-800/80 rounded-2xl p-4 flex items-center justify-around text-center text-xs">
              <div>
                <span className="text-[10px] text-zinc-400 block font-semibold">B&W Printing</span>
                <strong className="text-sm text-white font-mono">₹{shop?.bw_rate || 10} / page</strong>
              </div>
              <div className="w-px h-8 bg-zinc-800" />
              <div>
                <span className="text-[10px] text-zinc-400 block font-semibold">Color Printing</span>
                <strong className="text-sm text-amber-400 font-mono">₹{shop?.color_rate || 20} / page</strong>
              </div>
            </div>
          </div>
        )}

        {/* ============================================================ */}
        {/* STEP 2: ENHANCE & ADJUST                                      */}
        {/* ============================================================ */}
        {currentStep === 2 && activeItem && (
          <div className="flex flex-col gap-4 animate-fade-in">
            {/* Original PDF Notice */}
            {isPdfUpload && !hasUserEditedDocument && (
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-2xl p-3 flex items-center justify-between text-xs text-emerald-300">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-emerald-400 shrink-0" />
                  <span>Original vector PDF preserved for maximum print sharpness.</span>
                </div>
                <span className="text-[10px] font-mono bg-emerald-500/20 px-2 py-0.5 rounded text-emerald-300 truncate max-w-[150px]">
                  {rawUploadedFile?.name}
                </span>
              </div>
            )}

            {/* ID Card Front / Back Tabs */}
            {mode === "id_card" && (
              <div className="grid grid-cols-2 gap-2 bg-zinc-900 border border-zinc-800 p-1 rounded-xl">
                <button
                  type="button"
                  onClick={() => setActiveIdSlot("front")}
                  className={`py-2 rounded-lg font-bold text-xs transition-all ${
                    activeIdSlot === "front" ? "bg-amber-500 text-zinc-950 font-black" : "text-zinc-400"
                  }`}
                >
                  Front Side {idFront ? "✓" : ""}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setActiveIdSlot("back");
                    if (!idBack) cameraInputRef.current?.click();
                  }}
                  className={`py-2 rounded-lg font-bold text-xs transition-all ${
                    activeIdSlot === "back" ? "bg-amber-500 text-zinc-950 font-black" : "text-zinc-400"
                  }`}
                >
                  Back Side {idBack ? "✓" : "+ Add"}
                </button>
              </div>
            )}

            {/* Document Multi-Page Strip & Controls */}
            {mode === "document" && pages.length > 1 && (
              <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-3 flex flex-col gap-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-bold text-zinc-300">
                    Pages ({activePageIndex + 1} of {pages.length})
                  </span>
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      disabled={activePageIndex === 0}
                      onClick={() => handleMovePage(-1)}
                      className="p-1.5 rounded-lg bg-zinc-800 text-zinc-400 hover:text-white disabled:opacity-30"
                      title="Move Page Left"
                    >
                      <ArrowLeft className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      disabled={activePageIndex === pages.length - 1}
                      onClick={() => handleMovePage(1)}
                      className="p-1.5 rounded-lg bg-zinc-800 text-zinc-400 hover:text-white disabled:opacity-30"
                      title="Move Page Right"
                    >
                      <ArrowRight className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={handleDuplicatePage}
                      className="p-1.5 rounded-lg bg-zinc-800 text-zinc-400 hover:text-white"
                      title="Duplicate Page"
                    >
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeletePage(activePageIndex)}
                      className="p-1.5 rounded-lg bg-zinc-800 text-zinc-400 hover:text-red-400"
                      title="Delete Page"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="flex items-center gap-1 px-2 py-1 rounded-lg bg-amber-500/20 text-amber-400 text-[11px] font-bold border border-amber-500/30"
                    >
                      <Plus className="w-3 h-3" />
                      <span>Add</span>
                    </button>
                  </div>
                </div>

                {/* Thumbnails Carousel */}
                <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-none">
                  {pages.map((p, idx) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setActivePageIndex(idx)}
                      className={`relative w-14 aspect-[0.707] rounded-lg border-2 overflow-hidden shrink-0 transition-all ${
                        activePageIndex === idx
                          ? "border-amber-400 scale-105 shadow-md shadow-amber-500/20"
                          : "border-zinc-800 opacity-60 hover:opacity-100"
                      }`}
                    >
                      <img src={p.previewUrl} alt={`P.${idx + 1}`} className="w-full h-full object-contain bg-zinc-950" />
                      <span className="absolute bottom-0.5 right-0.5 text-[8px] bg-black/80 px-1 rounded font-bold text-white">
                        {idx + 1}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Main Document Preview Box with Before/After */}
            <div className="relative bg-zinc-900 border border-zinc-800 rounded-3xl p-3 shadow-xl flex flex-col items-center overflow-hidden">
              {/* Top Quality Badge, Rotate & Crop Button */}
              <div className="w-full flex items-center justify-between px-2 pb-2 text-xs">
                {activeItem.quality && (
                  <span
                    className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold border ${
                      activeItem.quality.status === "excellent"
                        ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/30"
                        : activeItem.quality.status === "good"
                        ? "bg-amber-500/10 text-amber-400 border-amber-500/30"
                        : "bg-rose-500/10 text-rose-400 border-rose-500/30"
                    }`}
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-current" />
                    <span>{activeItem.quality.label}</span>
                  </span>
                )}

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleRotatePage}
                    className="p-1.5 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-zinc-300 transition-colors"
                    title="Rotate 90°"
                  >
                    <RotateCw className="w-3.5 h-3.5" />
                  </button>

                  <button
                    type="button"
                    onClick={() => setCropEditorItem(activeItem)}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-xl bg-amber-500 text-zinc-950 font-bold text-xs hover:brightness-110 active:scale-95 shadow-md shadow-amber-500/20 transition-all cursor-pointer"
                  >
                    <Crop className="w-3.5 h-3.5" />
                    <span>Crop & Deskew</span>
                  </button>
                </div>
              </div>

              {/* Display Canvas Image */}
              <div className="relative w-full max-h-[380px] flex items-center justify-center bg-zinc-950 rounded-2xl overflow-hidden p-2">
                <img
                  src={
                    isHoldingOriginal
                      ? activeItem.originalCanvas?.toDataURL()
                      : activeItem.previewUrl
                  }
                  alt="Enhanced Document"
                  className="max-h-[360px] w-auto object-contain rounded-lg shadow-md"
                />

                {isHoldingOriginal && (
                  <div className="absolute top-4 left-4 px-3 py-1 bg-black/80 backdrop-blur-md rounded-full text-[11px] font-bold text-amber-400 border border-amber-500/30">
                    Original Raw View
                  </div>
                )}
              </div>

              {/* Compare Button */}
              <div className="w-full flex items-center justify-between pt-2 px-2">
                <button
                  type="button"
                  onMouseDown={() => setIsHoldingOriginal(true)}
                  onMouseUp={() => setIsHoldingOriginal(false)}
                  onTouchStart={() => setIsHoldingOriginal(true)}
                  onTouchEnd={() => setIsHoldingOriginal(false)}
                  className="text-[11px] text-zinc-400 hover:text-amber-400 font-bold flex items-center gap-1 transition-colors"
                >
                  <Eye className="w-3.5 h-3.5" />
                  <span>Hold for Original</span>
                </button>

                <div className="flex items-center gap-3">
                  {mode === "document" && pages.length > 1 && (
                    <button
                      type="button"
                      onClick={handleApplyToAllPages}
                      className="text-[11px] text-amber-400 font-bold hover:underline"
                    >
                      Apply to All Pages
                    </button>
                  )}

                  <button
                    type="button"
                    onClick={() => setShowFineSliders(!showFineSliders)}
                    className="text-[11px] text-zinc-400 hover:text-white font-bold flex items-center gap-1 transition-colors"
                  >
                    <Sliders className="w-3.5 h-3.5 text-amber-400" />
                    <span>{showFineSliders ? "Hide Sliders" : "Fine Sliders"}</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Filter Presets Carousel */}
            <div className="flex flex-col gap-1.5">
              <span className="text-xs font-bold text-zinc-300">Enhancement Filters (Optional):</span>
              <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-none">
                {PRESETS.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => updateActiveItem({ preset: p.id })}
                    className={`px-3 py-2 rounded-xl text-xs font-bold shrink-0 transition-all ${
                      activeItem.preset === p.id
                        ? "bg-amber-500 text-zinc-950 shadow-md shadow-amber-500/20 scale-105"
                        : "bg-zinc-900 border border-zinc-800 text-zinc-300 hover:text-white"
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Fine Sliders Drawer (Optional) */}
            {showFineSliders && (
              <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-4 flex flex-col gap-3 text-xs animate-scale-in">
                <div className="flex flex-col gap-1">
                  <div className="flex justify-between font-semibold text-zinc-300">
                    <span>Brightness</span>
                    <span>{activeItem.brightness || 0}</span>
                  </div>
                  <input
                    type="range"
                    min="-80"
                    max="80"
                    value={activeItem.brightness || 0}
                    onChange={(e) => updateActiveItem({ brightness: parseInt(e.target.value, 10) })}
                    className="accent-amber-500"
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <div className="flex justify-between font-semibold text-zinc-300">
                    <span>Contrast</span>
                    <span>{activeItem.contrast || 0}</span>
                  </div>
                  <input
                    type="range"
                    min="-80"
                    max="80"
                    value={activeItem.contrast || 0}
                    onChange={(e) => updateActiveItem({ contrast: parseInt(e.target.value, 10) })}
                    className="accent-amber-500"
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <div className="flex justify-between font-semibold text-zinc-300">
                    <span>Sharpness</span>
                    <span>{activeItem.sharpness || 0}%</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={activeItem.sharpness || 0}
                    onChange={(e) => updateActiveItem({ sharpness: parseInt(e.target.value, 10) })}
                    className="accent-amber-500"
                  />
                </div>
              </div>
            )}

            {/* Next Step Action */}
            <div className="flex items-center justify-between gap-3 pt-2">
              <button
                type="button"
                onClick={() => setCurrentStep(1)}
                className="px-4 py-2.5 rounded-xl bg-zinc-900 border border-zinc-800 text-zinc-400 font-bold text-xs hover:text-white"
              >
                Back
              </button>
              <button
                type="button"
                onClick={() => setCurrentStep(3)}
                className="flex-1 flex items-center justify-center gap-1.5 py-3 rounded-xl bg-gradient-to-r from-amber-400 via-amber-500 to-amber-600 text-zinc-950 font-black text-xs shadow-lg shadow-amber-500/20 active:scale-95 transition-all cursor-pointer"
              >
                <span>Continue to Print Setup</span>
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* ============================================================ */}
        {/* STEP 3: PRINT SETUP & PHYSICAL A4 PREVIEW                    */}
        {/* ============================================================ */}
        {currentStep === 3 && activeItem && (
          <div className="flex flex-col gap-5 animate-fade-in">
            {/* Print Type (B&W vs Color) */}
            <div className="grid grid-cols-2 gap-3 bg-zinc-900 border border-zinc-800 p-1.5 rounded-2xl">
              <button
                type="button"
                onClick={() => setPrintType("bw")}
                className={`py-3 rounded-xl font-black text-xs transition-all flex flex-col items-center gap-0.5 ${
                  printType === "bw"
                    ? "bg-zinc-800 text-white border border-zinc-600 shadow-md"
                    : "text-zinc-400 hover:text-white"
                }`}
              >
                <span>B&W Xerox</span>
                <span className="text-[10px] text-zinc-400 font-mono">₹{shop?.bw_rate || 10} / page</span>
              </button>
              <button
                type="button"
                onClick={() => setPrintType("color")}
                className={`py-3 rounded-xl font-black text-xs transition-all flex flex-col items-center gap-0.5 ${
                  printType === "color"
                    ? "bg-gradient-to-r from-amber-500 to-amber-600 text-zinc-950 shadow-md shadow-amber-500/20"
                    : "text-zinc-400 hover:text-white"
                }`}
              >
                <span>Color Print</span>
                <span className="text-[10px] text-zinc-900 font-mono font-bold">₹{shop?.color_rate || 20} / page</span>
              </button>
            </div>

            {/* Copies & Margin Adjustments */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-4 grid grid-cols-2 gap-4 text-xs">
              <div className="flex flex-col gap-1">
                <span className="font-semibold text-zinc-300">Number of Copies:</span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setCopies((c) => Math.max(1, c - 1))}
                    className="w-8 h-8 rounded-lg bg-zinc-800 text-white font-black text-sm flex items-center justify-center hover:bg-zinc-700"
                  >
                    -
                  </button>
                  <span className="font-mono text-base font-bold text-white w-6 text-center">{copies}</span>
                  <button
                    type="button"
                    onClick={() => setCopies((c) => Math.min(50, c + 1))}
                    className="w-8 h-8 rounded-lg bg-zinc-800 text-white font-black text-sm flex items-center justify-center hover:bg-zinc-700"
                  >
                    +
                  </button>
                </div>
              </div>

              <div className="flex flex-col gap-1">
                <span className="font-semibold text-zinc-300">Page Fitting:</span>
                <select
                  value={a4FitMode}
                  onChange={(e) => setA4FitMode(e.target.value)}
                  className="px-3 py-2 rounded-xl bg-zinc-950 border border-zinc-800 text-white text-xs outline-none focus:border-amber-500/60"
                >
                  <option value="fit">Proportional Fit (Recommended)</option>
                  <option value="fill">Fill Entire Page</option>
                  <option value="original">Original 1:1 Scale</option>
                </select>
              </div>
            </div>

            {/* Order Summary Box */}
            <div className="bg-gradient-to-r from-amber-500/10 to-amber-600/10 border border-amber-500/30 rounded-2xl p-4 flex items-center justify-between">
              <div>
                <span className="text-[10px] uppercase font-bold tracking-wider text-amber-400 block">Total Printing Estimate</span>
                <span className="text-xs text-zinc-300">
                  {totalSheets} {totalSheets === 1 ? "page" : "pages"} ({printType.toUpperCase()})
                </span>
              </div>
              <div className="text-right">
                <strong className="text-xl font-black text-amber-400 font-mono">₹{totalCost}</strong>
                <span className="text-[10px] text-zinc-500 block">Pay at shop counter</span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-3 pt-2">
              <button
                type="button"
                onClick={() => setCurrentStep(2)}
                className="px-4 py-3 rounded-xl bg-zinc-900 border border-zinc-800 text-zinc-400 font-bold text-xs hover:text-white"
              >
                Back
              </button>
              <button
                type="button"
                disabled={submitting}
                onClick={handleSubmitJob}
                className="flex-1 py-3 px-6 rounded-xl bg-gradient-to-r from-amber-400 via-amber-500 to-amber-600 hover:brightness-110 active:scale-95 text-zinc-950 font-black text-xs shadow-lg shadow-amber-500/25 transition-all cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <Printer className="w-4 h-4" />
                <span>{submitting ? "Sending to Printer..." : "Submit Print Job"}</span>
              </button>
            </div>
          </div>
        )}

        {/* ============================================================ */}
        {/* STEP 4: SUBMITTED CONFIRMATION                                */}
        {/* ============================================================ */}
        {currentStep === 4 && (
          <div className="bg-zinc-900 border border-emerald-500/30 rounded-3xl p-6 text-center flex flex-col items-center gap-4 animate-scale-in shadow-2xl">
            <div className="w-16 h-16 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center text-emerald-400 shadow-lg shadow-emerald-500/20">
              <CheckCircle2 className="w-8 h-8" />
            </div>

            <div>
              <h2 className="text-lg font-black text-white">Print Job Dispatched!</h2>
              <p className="text-xs text-zinc-400 mt-1 max-w-sm">
                Your document has been sent directly to the automated counter printer at{" "}
                <strong className="text-white">{shop?.shop_name_english || "PrintQ Shop"}</strong>.
              </p>
            </div>

            <div className="bg-zinc-950 border border-zinc-800 rounded-2xl p-4 w-full max-w-sm flex flex-col gap-2 text-xs text-left">
              <div className="flex justify-between">
                <span className="text-zinc-400">Total Sheets:</span>
                <strong className="text-white font-mono">{totalSheets}</strong>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-400">Print Type:</span>
                <strong className="text-amber-400 uppercase">{printType}</strong>
              </div>
              <div className="flex justify-between border-t border-zinc-800 pt-2 text-sm font-bold">
                <span className="text-zinc-300">Amount Due:</span>
                <span className="text-emerald-400 font-mono">₹{totalCost}</span>
              </div>
            </div>

            <button
              type="button"
              onClick={() => {
                setPages([]);
                setIdFront(null);
                setIdBack(null);
                setRawUploadedFile(null);
                setIsPdfUpload(false);
                setHasUserEditedDocument(false);
                setCurrentStep(1);
              }}
              className="mt-2 px-6 py-2.5 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-white font-bold text-xs transition-colors"
            >
              Print Another Document
            </button>
          </div>
        )}
      </main>

      {/* Interactive 4-Corner Crop & Perspective Deskew Modal */}
      {cropEditorItem && (
        <DocumentCropEditor
          sourceCanvas={cropEditorItem.originalCanvas}
          targetMode={mode}
          onConfirm={handleCropConfirmed}
          onCancel={() => setCropEditorItem(null)}
        />
      )}

      {/* Footer */}
      <footer className="px-4 py-3 border-t border-zinc-800/60 text-center text-[10px] text-zinc-500">
        PrintQ • Scan. Enhance. Print. by JSM DIGITAL SOLUTIONS
      </footer>
    </div>
  );
}